import { useContext } from 'react'
import { useIntl } from 'react-intl'

import { MessageContainer } from './common'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import { ModalElementContext } from 'contexts/ModalProvider'
import useOfferList from 'hooks/Offers/useOfferList'
import CarouselCTACard from 'tripPlanner/components/Bookmark/Carousels/CarouselCTACard'
import OfferItemsCarousel from 'tripPlanner/components/Bookmark/Carousels/OfferItemCarousel'
import SelectTourType from 'tripPlanner/components/TripModal/TripItems/Variety/SelectTourType'
import TourCTA from 'tripPlanner/components/TripSummary/CallToActionButtons/TourCTA'
import { useLEPlaceByCoordinates } from 'tripPlanner/hooks/api/place'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'

interface Props {
  lat: number
  lng: number
  item: TripItem
  showSearch: boolean
}

const TYPES: Array<App.Tours.V1OfferType | App.Tours.V2ParentOfferType> = [
  'tour',
  'tour_v2',
]

function NearbyToursModal({ lat, lng, item, showSearch }: Props) {
  const modalElement = useContext<any>(ModalElementContext)
  const openModal = useOpenModal()
  const intl = useIntl()

  const recommendedOffersTitle = intl.formatMessage({
    id: 'l185l7ERvHPVcoUmK0VO',
    defaultMessage: 'Recommended Offers',
    description: 'Title for the recommended tours modal in trip planner',
  })

  const findingOffersMessage = intl.formatMessage({
    id: '5fDnW1JunquczqUOTzml',
    defaultMessage: 'Finding the best offers for you',
    description: 'Loading message while fetching recommended tours in the trip planner item editor modal',
  })

  const noToursMessage = intl.formatMessage({
    id: 'AIVGUeOyxUyo8qT0Z1PC',
    defaultMessage: 'Sorry, we can’t find anything nearby',
    description: 'Empty state message when no nearby tours are available',
  })

  const searchToursLabel = intl.formatMessage({
    id: 'Iy-gUsYUyFJjKpUyPDKG',
    defaultMessage: 'Search all Tours & Cruises',
    description: 'Button label to search for tours and cruises when no nearby results found',
  })
  const { data: place, isLoading: fetchingPlace } = useLEPlaceByCoordinates(
    lat,
    lng,
  )

  const offerList = useOfferList(
    {
      destinationId: place?.id,
      searchNearby: true,
      offerTypes: TYPES,
      frontPageVisibleOnly: true,
    },
    { disabled: fetchingPlace },
  )

  const isLoading = fetchingPlace || offerList.fetching

  const carouselCtaCard = showSearch ? (
    <CarouselCTACard>
      <TourCTA
        key="ctaCard"
        tripId={item.tripId}
        beforeClick={modalElement.resolve}
      />
    </CarouselCTACard>
  ) : undefined

  return (
    <Modal title={recommendedOffersTitle} onClose={modalElement.resolve} size="L">
      <VerticalSpacer gap={16}>
        {isLoading && (
          <MessageContainer>
            <BodyText
              align="center"
              colour="neutral-one"
              variant="large"
              weight="bold"
            >
              {findingOffersMessage}
            </BodyText>
            <LoadingIndicator inline />
          </MessageContainer>
        )}
        {!isLoading && offerList.offerIds.length === 0 && (
          <MessageContainer>
            <BodyText
              align="center"
              colour="neutral-one"
              variant="large"
              weight="bold"
            >
              {noToursMessage}
            </BodyText>
            {showSearch && (
              <TextButton
                kind="primary"
                fit="auto"
                onClick={() => {
                  modalElement.resolve()
                  openModal(SelectTourType, {
                    tripId: item.tripId,
                  })
                }}
              >
                {searchToursLabel}
              </TextButton>
            )}
          </MessageContainer>
        )}
        {!isLoading && offerList.offerIds.length > 0 && (
          <OfferItemsCarousel
            offerIds={offerList.offerIds.slice(0, 4)}
            tripId={item.tripId}
            sectionId={tripItemSelectionId(item.id)}
            ctaCard={carouselCtaCard}
          />
        )}
      </VerticalSpacer>
    </Modal>
  )
}

export default NearbyToursModal
